import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LoginForm from '@/components/auth/LoginForm';
import {
  Headphones,
  Zap,
  BookOpen,
  BarChart3,
  Globe,
  ArrowRight,
} from 'lucide-react';
import Button from '@/components/ui/Button';

const features = [
  {
    icon: <Zap className="h-5 w-5 md:h-6 md:w-6" />,
    title: 'Быстрый ответ',
    description: 'Отвечаем за минуты. Команда на связи без очередей и пауз.',
    accent: 'rgba(91,124,255,0.55)',
  },
  {
    icon: <BookOpen className="h-5 w-5 md:h-6 md:w-6" />,
    title: 'База знаний',
    description: 'Готовые инструкции и статьи — часть вопросов закрываете сами.',
    accent: 'rgba(139,92,246,0.55)',
  },
  {
    icon: <BarChart3 className="h-5 w-5 md:h-6 md:w-6" />,
    title: 'Статус онлайн',
    description: 'Каждый тикет на виду: этап, исполнитель, сроки.',
    accent: 'rgba(52,211,153,0.55)',
  },
  {
    icon: <Globe className="h-5 w-5 md:h-6 md:w-6" />,
    title: 'Любой канал',
    description: 'Портал, e-mail, Telegram, ВК — пишите как удобно.',
    accent: 'rgba(251,191,36,0.55)',
  },
];

const HomePage: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  return (
    <div className="min-h-screen bg-bg-primary">
      <header className="border-b border-line">
        <div className="mx-auto flex h-14 md:h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <Headphones className="h-5 w-5 text-fg-primary" />
            <span className="text-lg md:text-xl font-bold text-fg-primary tracking-[-0.02em]">Support CRM</span>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-bg-card px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-fg-muted">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            On-call · 24/7
          </span>
        </div>
      </header>

      <section className="relative">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[360px] sm:hidden"
          style={{
            background:
              'radial-gradient(60% 65% at 50% 0%, rgba(91,124,255,0.16), transparent 70%)',
          }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-7xl px-5 pt-12 pb-8 sm:px-6 sm:py-20 md:py-28 lg:px-8 lg:py-36">
          <div className="mx-auto max-w-3xl text-center animate-fade-in">
            <div className="mb-6 flex flex-col items-center gap-3 sm:hidden">
              <span className="font-mono text-[10px] font-semibold tracking-[0.32em] text-fg-muted">
                CLUB:CORE · SUPPORT
              </span>
              <span
                aria-hidden
                className="h-px w-8 bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent"
              />
            </div>
            <h1 className="text-[44px] font-black leading-[0.96] tracking-[-0.045em] text-fg-primary sm:text-4xl sm:font-extrabold sm:tracking-[-0.035em] md:text-5xl lg:text-6xl">
              Техническая поддержка
            </h1>
            <div className="mx-auto mt-4 h-[2px] w-12 rounded-full bg-gradient-to-r from-emerald-400/80 via-emerald-400/30 to-transparent sm:hidden" aria-hidden />
            <p className="mt-4 md:mt-6 text-[14.5px] leading-[1.55] text-fg-secondary sm:text-xl sm:leading-relaxed sm:text-zinc-500">
              Один портал — обращения, база знаний и помощь специалистов.
              <span className="mt-1 block text-fg-muted sm:mt-0 sm:inline sm:text-zinc-500"> На связи без очередей.</span>
            </p>
            <div className="mt-6 md:mt-10 flex flex-col items-center gap-4">
              {!showLogin && (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => setShowLogin(true)}
                  className="cc-accent-grad cc-cta cc-glow w-full rounded-2xl text-accent-text shadow-[0_18px_42px_-18px_var(--accent-border)] sm:w-auto sm:px-12"
                  icon={<ArrowRight className="h-5 w-5" />}
                >
                  Войти в систему
                </Button>
              )}
              {/* Форма входа разворачивается прямо здесь (grid 0fr→1fr),
                  без перехода на отдельную страницу. */}
              <div className={`cc-login-unfold w-full max-w-[420px] text-left ${showLogin ? 'is-open' : ''}`}>
                <div className="cc-login-unfold__inner">
                  {showLogin && <LoginForm onClose={() => setShowLogin(false)} autoFocus />}
                </div>
              </div>
            </div>
            <p className="mt-4 text-center text-[12.5px] text-fg-muted sm:text-sm">
              Доступ выдаёт партнёр, подключивший ваш клуб к Club:Core. Используйте полученные логин и пароль для входа.
            </p>
          </div>
        </div>
      </section>

      <section className="relative pt-4 pb-10 md:py-20 sm:py-28">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-line bg-bg-card p-5 md:p-10 lg:p-12">
            <div className="mx-auto max-w-2xl text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-line bg-[color:var(--fill-2)] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-fg-muted md:hidden">
                Возможности
              </span>
              <h2 className="mt-3 md:mt-0 text-[26px] sm:text-3xl md:text-3xl font-bold leading-[1.08] tracking-[-0.025em] text-fg-primary sm:text-4xl">
                Всё для удобной работы
              </h2>
              <p className="mt-3 md:mt-4 text-[14px] sm:text-base md:text-lg leading-relaxed text-fg-secondary">
                Современные инструменты для эффективного взаимодействия с нашей командой поддержки
              </p>
            </div>

            <div className="mt-8 md:mt-12 grid gap-3 md:gap-6 grid-cols-2 lg:grid-cols-4">
              {features.map((feature, idx) => (
                <div
                  key={feature.title}
                  className="group relative aspect-square overflow-hidden rounded-2xl border border-line bg-[color:var(--fill-1)] p-4 md:p-6 transition-all duration-300 hover:border-[color:var(--border-color-hi)] md:aspect-auto flex flex-col"
                >
                  <span
                    className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
                    style={{ background: `linear-gradient(90deg, transparent, ${feature.accent.replace('0.55', '0.8')}, transparent)` }}
                    aria-hidden
                  />
                  <span className="pointer-events-none absolute right-3 top-3 font-mono text-[10px] font-semibold tracking-[0.16em] text-fg-muted md:right-4 md:top-4">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <div className="mb-3 md:mb-4 relative flex h-11 w-11 md:h-12 md:w-12 items-center justify-center rounded-xl border border-line bg-[color:var(--fill-2)]">
                    <span style={{ color: feature.accent.replace('0.55', '0.95') }}>
                      {feature.icon}
                    </span>
                  </div>
                  <h3 className="mb-1.5 text-[15px] md:text-lg font-semibold leading-snug text-fg-primary">{feature.title}</h3>
                  <p className="text-[12.5px] md:text-sm leading-snug text-fg-secondary md:leading-relaxed line-clamp-3 md:line-clamp-none">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-line py-6 md:py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-[19px] md:text-2xl font-bold text-fg-primary sm:text-3xl tracking-[-0.02em]">
            Нужна помощь? Мы на связи!
          </h2>
          <p className="mt-1.5 md:mt-3 text-[13.5px] md:text-lg text-fg-secondary">
            Войдите по выданным учётным данным — оператор уже ждёт обращения.
          </p>
          <div className="mt-4 md:mt-8 flex justify-center">
            <Link to="/login" className="w-full sm:w-auto">
              <Button
                variant="primary"
                size="lg"
                className="cc-accent-grad cc-cta cc-glow w-full rounded-2xl text-accent-text shadow-[0_18px_42px_-18px_var(--accent-border)] sm:w-auto sm:px-12"
                icon={<ArrowRight className="h-5 w-5" />}
              >
                Войти в систему
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-line py-4 md:py-8">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2.5">
            <Headphones className="h-4 w-4 text-fg-primary" />
            <span className="font-semibold text-fg-primary">Support CRM</span>
          </div>
          <p className="mt-3 text-sm text-zinc-600">
            &copy; {new Date().getFullYear()} Support CRM. Все права защищены.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
